import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/styles/theme.css';
import './pages/Notifications/Notifications.css';
import './App.css';

import { store } from './store/store';
import { ThemeProvider } from './contexts/ThemeContext';
import { setStore } from './services/socket';

// Pages
import LandingPage from './pages/Landing/LandingPage';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Verification from './pages/Auth/Verification';
import ProfileCompletion from './pages/Auth/ProfileCompletion';
import Home from './pages/Home/Home';
import Profile from './pages/Profile/Profile';
import Messages from './pages/Messages/Messages';
import Discover from './pages/Discover/Discover';
import Dashboard from './pages/Dashboard/Dashboard';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import AdminActivities from './pages/AdminActivities/AdminActivities';
import AdminNotifications from './pages/AdminNotifications/AdminNotifications';
import AdminCertifications from './pages/AdminCertifications/AdminCertifications';
import AdminPayments from './pages/AdminPayments/AdminPayments';
import AdminDiscussions from './pages/AdminDiscussions/AdminDiscussions';
import SuperAdminDashboard from './pages/SuperAdminDashboard/SuperAdminDashboard';
import UserManagement from './pages/UserManagement';
import Stories from './pages/Stories';
import Notifications from './pages/Notifications/Notifications';
import Loves from './pages/Loves/Loves';
import Settings from './pages/Settings/Settings';
import Certification from './pages/Settings/Certification';
import WalletPage from './pages/Wallet/WalletPage';
import DiamantStore from './pages/Wallet/DiamantStore';
import GiftsPage from './pages/Gifts/GiftsPage';

// Components
import PrivateRoute from './components/common/PrivateRoute';
import Layout from './components/layout/Layout';

// Root redirect component - redirects authenticated users to home
const RootRedirect = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  return isAuthenticated ? <Navigate to="/home" replace /> : <LandingPage />;
};

const AppContent = () => {
  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <Routes>
        {/* Routes publiques */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify" element={<Verification />} />

        {/* Routes protégées */}
        <Route element={<PrivateRoute />}>
          <Route path="/complete-profile" element={<ProfileCompletion />} />
          <Route element={<Layout />}>
            <Route path="/home" element={<Home />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/loves" element={<Loves />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/certification" element={<Certification />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/wallet/store" element={<DiamantStore />} />
            <Route path="/gifts" element={<GiftsPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/activities" element={<AdminActivities />} />
            <Route path="/admin/notifications" element={<AdminNotifications />} />
            <Route path="/admin/certifications" element={<AdminCertifications />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/discussions" element={<AdminDiscussions />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/superadmin/dashboard" element={<SuperAdminDashboard />} />
          </Route>
        </Route>

        {/* Redirection par défaut */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

function App() {
  // Set the store for socket service
  setStore(store);

  return (
    <Provider store={store}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </GoogleOAuthProvider>
    </Provider>
  );
}

export default App;
